import api from '../config/api';
import {
  fusionarConceptosPlameEInternos as fusionarConceptosPlameEInternosUtil,
  normalizarConceptoParaGuardar as normalizarConceptoParaGuardarUtil,
} from '../utils/recalculoConsideracionesConceptos';

/**
 * Service para manejar las consideraciones de conceptos de planilla
 * Optimización: Enviar conceptos solo la primera vez, luego usar datos guardados
 */
class ConsideracionesConceptosService {

  normalizarConceptoParaGuardar(concepto, tipoConceptoFallback = null) {
    return normalizarConceptoParaGuardarUtil(concepto, tipoConceptoFallback);
  }

  fusionarConceptosPlameEInternos(empleado) {
    return fusionarConceptosPlameEInternosUtil(empleado);
  }

  /**
   * Verificar si ya existen conceptos guardados para una planilla
   */
  async verificarConceptosExistentes(planillaId) {
    try {
      console.log(`🔍 Verificando conceptos existentes para planilla ${planillaId}...`);
      const response = await api.get(`/planillas/${planillaId}/verificar-conceptos`);
      console.log('✅ Verificación de conceptos completada:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error verificando conceptos existentes:', error);
      throw error;
    }
  }

  /**
   * Enviar conceptos de empleados al backend para guardar.
   * Incluye destino_ajuste / monto_interna (planilla alternativa).
   *
   * Acepta:
   * - empleados con conceptos ya normalizados (desde actualizarConceptosEnBackend)
   * - empleados del maestro con conceptos + conceptos_internos
   */
  async guardarConceptosPlanilla(planillaId, empleados) {
    try {
      console.log(`💾 Enviando conceptos de ${empleados.length} empleados para planilla ${planillaId}...`);

      const empleadosParaEnviar = empleados.map((empleado) => {
        const conceptos = empleado.conceptos;
        const modo = empleado.modo || 'reemplazar';
        const yaNormalizado =
          Array.isArray(conceptos)
          && (conceptos.length === 0 || conceptos.every((c) => c.plame_configuracion_id && c.tipo_concepto));

        // Recálculo parcial: no re-fusionar internos (eso ponía PLAME en 0 y duplicaba).
        const todosLosConceptos = (modo === 'parcial' || (yaNormalizado && !empleado.conceptos_internos?.length))
          ? (conceptos || []).map((c) => this.normalizarConceptoParaGuardar(c)).filter(Boolean)
          : this.fusionarConceptosPlameEInternos(empleado);

        return {
          id: empleado.id,
          modo,
          conceptos: todosLosConceptos,
          eliminar_plame_ids: empleado.eliminar_plame_ids || [],
        };
      });

      const empleadosConConceptos = empleadosParaEnviar.filter(
        (emp) => emp.conceptos.length > 0 || (emp.eliminar_plame_ids || []).length > 0
      );

      console.log(`📊 Resumen para envío:`, {
        empleados_totales: empleados.length,
        empleados_con_conceptos: empleadosConConceptos.length,
        total_conceptos: empleadosConConceptos.reduce((sum, emp) => sum + emp.conceptos.length, 0),
        eliminados: empleadosConConceptos.reduce((sum, emp) => sum + (emp.eliminar_plame_ids || []).length, 0),
      });

      if (empleadosConConceptos.length === 0) {
        throw new Error('No hay empleados con conceptos válidos para enviar');
      }

      const response = await api.post(`/planillas/${planillaId}/guardar-conceptos`, {
        empleados: empleadosConConceptos,
      });

      console.log('✅ Conceptos guardados exitosamente:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error guardando conceptos:', error);
      throw error;
    }
  }

  async obtenerDatosParaCalculo(planillaId) {
    try {
      console.log(`📥 Obteniendo datos guardados para cálculo de planilla ${planillaId}...`);
      const response = await api.get(`/planillas/${planillaId}/datos-calculo`);
      console.log('✅ Datos para cálculo obtenidos:', {
        empleados: response.data.data.empleados.length,
        estadisticas: response.data.data.estadisticas,
      });
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo datos para cálculo:', error);
      throw error;
    }
  }

  async eliminarConceptosPlanilla(planillaId) {
    try {
      console.log(`🗑️ Eliminando conceptos guardados de planilla ${planillaId}...`);
      const response = await api.delete(`/planillas/${planillaId}/conceptos`);
      console.log('✅ Conceptos eliminados exitosamente:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error eliminando conceptos:', error);
      throw error;
    }
  }

  async obtenerConceptosEmpleado(planillaId, empleadoId) {
    try {
      console.log(`📋 Obteniendo conceptos de empleado ${empleadoId} en planilla ${planillaId}...`);
      const response = await api.get(`/planillas/${planillaId}/empleados/${empleadoId}/conceptos`);
      console.log('✅ Conceptos de empleado obtenidos:', {
        empleado_id: empleadoId,
        total_conceptos: response.data.data.estadisticas?.total_conceptos || 0,
        ingresos: response.data.data.conceptos.ingresos.length,
        descuentos: response.data.data.conceptos.descuentos.length,
        aportes: response.data.data.conceptos.aportes.length,
      });
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo conceptos de empleado:', error);
      if (error.response?.status === 404) {
        return {
          success: false,
          data: {
            planilla_id: planillaId,
            empleado_id: empleadoId,
            conceptos: {
              ingresos: [],
              descuentos: [],
              aportes: [],
            },
          },
        };
      }
      throw error;
    }
  }

  async flujoOptimizadoConceptos(planillaId, empleados = null) {
    try {
      console.log(`🚀 === INICIANDO FLUJO OPTIMIZADO PARA PLANILLA ${planillaId} ===`);
      const verificacion = await this.verificarConceptosExistentes(planillaId);

      if (verificacion.data.tiene_conceptos) {
        console.log('✅ DATOS EXISTENTES ENCONTRADOS - Usando conceptos guardados');
        const datosGuardados = await this.obtenerDatosParaCalculo(planillaId);
        return {
          source: 'datos_guardados',
          datos: datosGuardados.data,
          info: verificacion.data,
        };
      }

      console.log('❌ NO EXISTEN DATOS GUARDADOS - Enviando conceptos al backend');
      if (!empleados || empleados.length === 0) {
        throw new Error('Se requieren datos de empleados para el primer envío');
      }

      const resultadoGuardado = await this.guardarConceptosPlanilla(planillaId, empleados);
      const datosGuardados = await this.obtenerDatosParaCalculo(planillaId);

      return {
        source: 'datos_nuevos',
        datos: datosGuardados.data,
        guardado: resultadoGuardado.data,
      };
    } catch (error) {
      console.error('❌ Error en flujo optimizado:', error);
      throw error;
    }
  }

  /**
   * Flags por empleado en consideraciones previas (p. ej. omitir descuentos PLAME → interna).
   */
  async obtenerFlagsEmpleado(planillaId) {
    try {
      const response = await api.get(`/planillas/${planillaId}/consideraciones-empleado`);
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo flags consideraciones empleado:', error);
      throw error;
    }
  }

  /**
   * Upsert flags por empleado.
   * @param {number} planillaId
   * @param {Array<{id:number, omitir_descuentos_plame_interna?:boolean, periodo_gratificacion_consu?:string, forma_pago_planilla?:string, fecha_desde_jornal?:string, aplicar_descuento_dominical?:boolean|null}>} empleados
   */
  async guardarFlagsEmpleado(planillaId, empleados) {
    try {
      const response = await api.post(`/planillas/${planillaId}/consideraciones-empleado`, {
        empleados,
      });
      return response.data;
    } catch (error) {
      console.error('❌ Error guardando flags consideraciones empleado:', error);
      throw error;
    }
  }

  async obtenerOverridesTareoEvento(planillaId) {
    try {
      const response = await api.get(`/planillas/${planillaId}/consideraciones-tareo-evento`);
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo overrides tareo evento:', error);
      throw error;
    }
  }

  /**
   * @param {number} planillaId
   * @param {{ overrides?: Array, eliminar?: Array }} payload
   */
  async guardarOverridesTareoEvento(planillaId, payload) {
    try {
      const response = await api.post(`/planillas/${planillaId}/consideraciones-tareo-evento`, payload);
      return response.data;
    } catch (error) {
      console.error('❌ Error guardando overrides tareo evento:', error);
      throw error;
    }
  }

  async obtenerTramosBoletaInterna(planillaId) {
    try {
      const response = await api.get(`/planillas/${planillaId}/consideraciones-boleta-interna-tramos`);
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo tramos boleta interna:', error);
      throw error;
    }
  }

  async guardarTramosBoletaInterna(planillaId, payload) {
    try {
      const response = await api.post(`/planillas/${planillaId}/consideraciones-boleta-interna-tramos`, payload);
      return response.data;
    } catch (error) {
      console.error('❌ Error guardando tramos boleta interna:', error);
      throw error;
    }
  }

  async obtenerTramosSueldoPlame(planillaId) {
    try {
      const response = await api.get(`/planillas/${planillaId}/consideraciones-sueldo-tramos`);
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo tramos de sueldo PLAME:', error);
      throw error;
    }
  }

  async guardarTramosSueldoPlame(planillaId, payload) {
    try {
      const response = await api.post(`/planillas/${planillaId}/consideraciones-sueldo-tramos`, payload);
      return response.data;
    } catch (error) {
      console.error('❌ Error guardando tramos de sueldo PLAME:', error);
      throw error;
    }
  }

  async restaurarConceptosOriginalesEmpleado(empleadoId) {
    try {
      console.log(`🔄 Solicitando restauración de conceptos originales para empleado ${empleadoId}...`);
      const response = await api.get(`/empleados/${empleadoId}/conceptos-originales`);
      console.log('✅ Conceptos originales obtenidos:', {
        empleado_id: empleadoId,
        total_conceptos: response.data.data.estadisticas?.total_conceptos || 0,
        ingresos: response.data.data.conceptos.ingresos.length,
        descuentos: response.data.data.conceptos.descuentos.length,
        aportes: response.data.data.conceptos.aportes.length,
      });
      return response.data;
    } catch (error) {
      console.error('❌ Error restaurando conceptos originales:', error);
      throw error;
    }
  }
}

export default new ConsideracionesConceptosService();
